/**
 * Instagram permission names, kept in a leaf module with no imports.
 *
 * The connect flow requests these, and the client-side capability derivations
 * read them back off a stored account. That second reader lives deep in
 * `entities/`, so the strings cannot sit beside the connect code without
 * dragging axios, the toast layer and the redux store in with them.
 */

const InstagramScopesBasic = [
  "instagram_basic",
  "instagram_manage_insights",
  "business_management",
];

export const InstagramScopeInbox = [
  "instagram_manage_messages",
  "pages_messaging",
];

export const RequiredInstagramScopes = [
  ...InstagramScopesBasic,
  ...InstagramScopeInbox,
];

export const InstagramScopeInboxComments = ["instagram_manage_comments"];

/**
 * SKE-4526. The one permission that authorises publishing through Meta's
 * Content Publishing API. The capability derivations key off this alone,
 * because first comment is a separate question with a separate permission.
 */
export const InstagramScopeContentPublish = "instagram_content_publish";

/**
 * SKE-4526 — the permissions that let Sked publish to Instagram through Meta's
 * Content Publishing API instead of driving a phone.
 *
 * These are deliberately NOT part of `RequiredInstagramScopes`. Their absence
 * means an account is not API-publishable yet; it does not mean the connection
 * is broken. Adding them to the required set would mark every existing
 * customer's working account as needing repair.
 *
 * `instagram_manage_comments` is already requested separately for Inbox, and
 * the publish path needs it too for first comment.
 */
export const InstagramScopePublishing = [
  InstagramScopeContentPublish,
  ...InstagramScopeInboxComments,
];

/**
 * Product tagging. Meta requires the app to be associated with a verified
 * business, which Dialogue Consulting has, and only checkout-enabled Shops can
 * actually use the tags. Requested here so the one consent covers it.
 */
export const InstagramScopeProductTagging = [
  "instagram_shopping_tag_products",
  "catalog_management",
];

/**
 * The permissions this module gates behind `enableInstagramPublishScopeRequest`,
 * and therefore the ones an account can hold while the next reconnect forgets
 * to ask for them.
 */
const GatedInstagramScopes = [
  ...InstagramScopePublishing,
  ...InstagramScopeProductTagging,
];

/**
 * SKE-4806 — the permissions a reconnect must keep asking for because the
 * account already holds them.
 *
 * `AccountModel.addInstagramAPItoken` replaces `session` and `longSession`
 * wholesale with a `$set`, so the stored permission set is exactly whatever the
 * last login requested and Facebook granted. A request narrower than what the
 * row already holds therefore does not leave the old permissions alone: it
 * destroys them. An account publishing through Meta's API silently stops being
 * able to, and the reconnect reports success.
 *
 * `features/instagram-campaign/campaign-scopes.ts` states this rule for the
 * campaign's pass, which asks unconditionally. The ordinary reconnect paths ask
 * only when `enableInstagramPublishScopeRequest` is on, so they need the
 * account's own holdings to top the request back up. Measured on 16 September
 * 2026: 22 of the 34 production rows holding `instagram_content_publish`
 * belonged to admins with that toggle off.
 *
 * PRESERVATION AT THE LEVEL OF THE CONSENT, WHICH IS THE WORKSPACE, NOT THE
 * ACCOUNT. One Facebook login covers every account it returns, so the callers
 * that reconnect a whole workspace hand in every account and get the union.
 * That means an account which has never held the publishing permission can see
 * it on the consent screen because a SIBLING account holds it, and could end up
 * storing it.
 *
 * That is deliberate and it is the lesser evil: a per-account request is not
 * something Facebook offers, and narrowing to the account being repaired would
 * downgrade its siblings in the same pass, which is the bug this exists to fix.
 * It stays contained because holding the scope is not sufficient to publish
 * through the API. `deriveInstagramApiPublishingCapability` refuses before it
 * ever reads the scope unless the admin's gate is on.
 *
 * What it never does is introduce the permission to a WORKSPACE that has never
 * held it anywhere. That is the line that matters while
 * `instagram_content_publish` is still in App Review.
 *
 * Reads `session.scopes` and `longSession.scopes` and nothing else, because
 * that is exactly what `hasIGScope` in `entities/accounts/libs/
 * get-account-statuses.ts` and the server's `hasScope` in
 * `services/instagram-publishing/capability.ts` read. A third definition of
 * "holds this scope" is how the three would come to disagree.
 *
 * Takes a structural type rather than `AccountType` so this file keeps its
 * no-imports property; its other consumer lives deep in `entities/`.
 */
export function preservedInstagramScopes(
  accounts:
    | readonly (
        | {
            session?: { scopes?: string[] | null } | null;
            longSession?: { scopes?: string[] | null } | null;
          }
        | null
        | undefined
      )[]
    | null
    | undefined
): string[] {
  const held = new Set<string>();
  for (const account of accounts || []) {
    for (const scope of account?.session?.scopes || []) held.add(scope);
    for (const scope of account?.longSession?.scopes || []) held.add(scope);
  }
  return GatedInstagramScopes.filter((scope) => held.has(scope));
}
